import React from 'react';
import { Link } from 'react-router-dom';
import PageSEO from '../components/PageSEO';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';

const TermsOfService: React.FC = () => {
  return (
    <div className="min-h-screen bg-aspen-warm-ivory dark:bg-aspen-night-sage">
      <PageSEO
        title="Terms of Service — Ask Aspen"
        description="Read the Ask Aspen terms of service. Understand your rights, SMS messaging terms, fair use policy, and service guidelines."
        canonicalPath="/terms"
        breadcrumbName="Terms of Service"
      />
      <SiteHeader />

      {/* Content */}
      <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="bg-white dark:bg-aspen-twilight-teal rounded-lg shadow-lg p-8">
          <h1 className="text-3xl font-bold text-aspen-deep-eucalyptus dark:text-aspen-pearl mb-6">
            Terms of Service
          </h1>

          <div className="prose prose-lg max-w-none text-aspen-deep-eucalyptus dark:text-aspen-pearl">
            <p className="text-sm text-aspen-sage-teal dark:text-aspen-gentle-teal mb-8">
              Last updated: 2025-10-18
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                1. Acceptance of Terms
              </h2>
              <p className="mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                By accessing or using Ask Aspen ("Service"), provided by Manatee Media LLC ("Ask Aspen," "we," "us," or "our"),
                you agree to these Terms of Service ("Terms"). If you do not agree, do not use the Service. We may update these
                Terms from time to time; the "Last updated" date reflects the current version, and continued use means you accept
                the updates.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                2. Eligibility; Accounts
              </h2>
              <p className="mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                You must be at least 18 years old (or the age of majority in your jurisdiction) or have verifiable parental consent.
                You are responsible for the accuracy of your account information and for all activity under your account.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                3. Service Description; No Emergency Use
              </h2>
              <p className="mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                Ask Aspen provides task management and reminders via SMS and a web application. The Service is not a substitute
                for emergency services and may not be used to request or dispatch emergency response.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                4. SMS Messaging; TCPA/TCR/CTIA Compliance
              </h2>
              <p className="mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                By providing your mobile number and opting in, you consent to receive automated texts (e.g., reminders, daily
                check-ins, service notices, account updates).
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                <li><strong>Frequency:</strong> Varies by usage; may include multiple messages per day.</li>
                <li><strong>Rates:</strong> Message/data rates may apply.</li>
                <li><strong>Opt-Out:</strong> Reply STOP to any message to unsubscribe.</li>
                <li><strong>Help:</strong> Reply HELP for support info.</li>
              </ul>
              <div className="bg-aspen-morning-mist dark:bg-aspen-moonlit-clay/20 p-6 rounded-lg mb-6">
                <p className="text-aspen-deep-eucalyptus dark:text-aspen-pearl leading-relaxed">
                  <strong>Disclosure (CTIA-style):</strong> "No mobile information will be shared with third parties/affiliates
                  for marketing/promotional purposes. All other categories exclude text-messaging originator opt-in data and
                  consent; this information will not be shared with any third parties."
                </p>
              </div>
              <ul className="list-disc pl-6 space-y-2 mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                <li><strong>Carriers:</strong> Major U.S. carriers are supported; availability may vary. Carriers are not liable
                for delayed or undelivered messages.</li>
                <li><strong>Your warranties:</strong> You are the authorized user of the number and grant express written consent
                under TCPA to receive automated texts. Consent is not required to purchase.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                5. Fair Use & Rate Limits
              </h2>
              <p className="mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                To keep the Service reliable for all users and economically viable for us, your access is subject to Fair Use,
                which includes per-account quotas, rate limits, and cost-based controls. We may, at our reasonable discretion,
                throttle, delay, limit, or suspend access where usage:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                <li>materially exceeds typical consumer usage patterns,</li>
                <li>imposes disproportionate costs on our messaging, compute, or infrastructure, or</li>
                <li>appears abusive, automated, or resold.</li>
              </ul>
              <p className="mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                We will attempt to notify you if we materially reduce your limits, and we may offer a paid plan or overage
                pricing where available. (See also §11 Fees.)
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                6. Prohibited Conduct
              </h2>
              <p className="mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                You agree not to: (a) violate laws; (b) infringe IP or privacy rights; (c) transmit malware or interfere with
                the Service; (d) scrape, resell, or provide unauthorized access; (e) send spam or harassing content; (f) attempt
                to bypass rate limits, quotas, or security; (g) reverse engineer the Service except as permitted by law.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                7. User Content & License
              </h2>
              <p className="mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                You retain ownership of the content you submit (e.g., tasks, reminders). You grant us a non-exclusive, worldwide,
                royalty-free license to host, process, transmit, and display that content solely to provide and improve the Service.
                You represent you have the rights to submit such content.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                8. Intellectual Property
              </h2>
              <p className="mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                All Service software, features, brand elements, and content (excluding your content) are owned by us or our
                licensors and are protected by IP laws. No rights are granted except as expressly stated in these Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                9. Third-Party Services and Carriers
              </h2>
              <p className="mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                The Service integrates with third parties (e.g., SMS carriers, hosting, analytics). We are not responsible for
                third-party failures or terms. SMS delivery depends on networks beyond our control; messages may be delayed or
                undelivered.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                10. Beta Features
              </h2>
              <p className="mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                From time to time, we may offer preview or beta features. These are provided AS IS, may be modified or discontinued,
                and may be subject to additional terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                11. Fees; Changes
              </h2>
              <p className="mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                The Service may be free during beta. We reserve the right to introduce paid plans, overage fees, or different
                usage tiers in the future. We will post pricing changes in advance and they will apply prospectively. If you do
                not agree, you must cease use before the effective date.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                12. Disclaimers
              </h2>
              <p className="mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE." TO THE FULLEST EXTENT PERMITTED BY LAW, WE DISCLAIM ALL
                WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
                WE DO NOT WARRANT UNINTERRUPTED OR ERROR-FREE OPERATION OR DELIVERY.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                13. Limitation of Liability
              </h2>
              <p className="mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE WILL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL,
                EXEMPLARY, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, DATA, GOODWILL, OR BUSINESS INTERRUPTION. OUR TOTAL
                LIABILITY FOR ALL CLAIMS RELATING TO THE SERVICE IN ANY 12-MONTH PERIOD IS LIMITED TO THE GREATER OF $100 OR THE
                AMOUNT YOU PAID TO US FOR THAT PERIOD.
              </p>
              <p className="mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl text-sm italic">
                (Some jurisdictions do not allow certain limitations; where prohibited, the limitation applies to the maximum
                extent permitted.)
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                14. Indemnification
              </h2>
              <p className="mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                You will defend, indemnify, and hold harmless Ask Aspen and Manatee Media LLC (and their officers, directors,
                employees, and agents) from claims arising out of: (a) your misuse of the Service; (b) your breach of these Terms;
                (c) your violation of law or third-party rights; or (d) content you submit.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                15. Termination
              </h2>
              <p className="mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                We may suspend or terminate the Service or your access at any time, including for suspected abuse or violation of
                these Terms (see Fair Use). You may stop using the Service at any time or text STOP to opt-out of SMS. Sections
                that by their nature survive (e.g., IP, disclaimers, limits, indemnity, governing law) will continue.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                16. Privacy
              </h2>
              <p className="mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                Your use is also governed by our <Link to="/privacy" className="text-aspen-sage-teal hover:underline">Privacy Policy</Link>,
                which describes how we collect and use personal data (including SMS opt-in records).
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                17. Governing Law; Venue
              </h2>
              <p className="mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                These Terms are governed by the laws of the State of Colorado, without regard to conflict-of-laws rules. Exclusive
                jurisdiction and venue lie in the state or federal courts located in Colorado, and you consent to personal
                jurisdiction there.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                18. Changes to the Service or Terms
              </h2>
              <p className="mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                We may change the Service or these Terms. For material changes, we will provide reasonable notice (e.g., by posting
                in-app or on our site). If you do not agree, stop using the Service before the effective date.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                19. Severability; Waiver; Entire Agreement
              </h2>
              <p className="mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                If any provision is unenforceable, the remainder remains in effect. Failure to enforce is not a waiver. These
                Terms plus the Privacy Policy are the entire agreement.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                20. Contact
              </h2>
              <div className="bg-aspen-morning-mist dark:bg-aspen-moonlit-clay/20 p-4 rounded-lg">
                <p className="text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                  <strong>Ask Aspen (Manatee Media LLC)</strong><br />
                  Email: manateemediallc@gmail.com<br />
                  SMS: (720) 914-1955
                </p>
              </div>
            </section>

            <section className="mb-8 border-t border-aspen-sage-teal/20 pt-8">
              <h2 className="text-2xl font-semibold mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                Fair Use Policy (Reasonable Use & Cost Control)
              </h2>

              <h3 className="text-xl font-semibold mb-3 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                Purpose
              </h3>
              <p className="mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                This Fair Use Policy ("FUP") ensures reliable service and prevents a small number of accounts from imposing
                disproportionate costs on our messaging and infrastructure.
              </p>

              <h3 className="text-xl font-semibold mb-3 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                Typical Use
              </h3>
              <p className="mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                Ask Aspen is intended for individual, personal productivity use. While active use varies, most users fall within
                a reasonable range of daily tasks/check-ins and reminders.
              </p>

              <h3 className="text-xl font-semibold mb-3 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                Cost-Based Controls
              </h3>
              <p className="mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                We may apply automatic or manual controls to any account that, in our reasonable judgment, causes or risks causing
                excessive cost or network load, including:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                <li>per-account message caps,</li>
                <li>rate limiting (throttling message creation, delivery, or check-ins),</li>
                <li>burst controls (limits on short-interval spikes),</li>
                <li>temporary suspension or queueing of traffic, and</li>
                <li>plan changes or overage pricing (where available), with notice before charges apply.</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                Examples of High-Volume/High-Cost Use
              </h3>
              <ul className="list-disc pl-6 space-y-2 mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                <li>Sustained message volumes materially above typical consumer usage patterns (e.g., automated task spam, bulk
                reminders, resale/relay of messages).</li>
                <li>Attempts to circumvent limits (multiple accounts, automation, scripting, or relays).</li>
                <li>Use cases that replicate a bulk messaging or notification service.</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                Process & Notice
              </h3>
              <p className="mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                Where feasible, we will notify you if we are applying controls and provide a path (e.g., upgrade plan, reduce
                volume) to restore normal operation. For abuse or security risk, we may apply controls immediately.
              </p>

              <h3 className="text-xl font-semibold mb-3 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                No Resale/Bulk
              </h3>
              <p className="mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                The Service may not be resold or used to provide messaging services to third parties without our written permission.
              </p>

              <h3 className="text-xl font-semibold mb-3 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                Interaction with Terms
              </h3>
              <p className="mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                This FUP forms part of the Terms. Violations of this FUP may result in throttling, suspension, or termination
                under §15.
              </p>
            </section>
          </div>
        </div>
      </div>

      <SiteFooter />
    </div>
  );
};

export default TermsOfService;
